import { Alert, Descriptions, Skeleton, Typography } from 'antd'
import { observer } from 'mobx-react-lite'

import { HistoryTimeline } from '@/Ship/Components/History'
import { formatDateTimeUk } from '@/Ship/Helpers/fieldViews'
import { classNames } from '@/Ship/Helpers/classNames'

import { fileExtension, formatBytes, itemsCount, KIND_LABEL, storageTitle } from '../Helpers/diskFormat'
import { diskHistoryEntries } from '../Helpers/diskHistoryEntries'
import { DiskNodeIcon } from './DiskNodeIcon'
import { DiskPerson } from './DiskPerson'

import type { DiskNode } from '../Contracts/DiskNode'
import type { DiskNodeInfoStore } from '../Stores/DiskNodeInfoStore'
import type { ReactNode } from 'react'

import styles from './Disk.module.css'

const { Title } = Typography

/** Шлях вузла: простір / папки. */
const pathOf = (node: DiskNode): string => {
    const root = node.storage ? storageTitle(node.storage) : ''
    const folders = (node.ancestors ?? []).filter((ancestor) => !ancestor.root).map((ancestor) => ancestor.name)

    return [root, ...folders].filter(Boolean).join(' / ')
}

/** Тип: «Документ · DOCX», «Папка». MIME — у підказці, а не в рядку: для людини він шум. */
const kindText = (node: DiskNode): string => {
    const ext = node.type === 'file' ? fileExtension(node.name) : ''

    return ext ? `${KIND_LABEL[node.kind]} · ${ext}` : KIND_LABEL[node.kind]
}

export interface DiskNodeInfoPanelProps {
    info: DiskNodeInfoStore
    /** Замість іконки в шапці — мініатюра (дровер «Властивості» для зображень). */
    lead?: ReactNode
    /** Кнопки під назвою: «Відкрити», «Скачати», «Перейменувати» (дровер). */
    actions?: ReactNode
    /** Скільки елементів у папці; `undefined` — рядка немає (переглядач файлу). */
    folderItems?: number | null
    /** Секція «Доступ» перед журналом (дровер «Властивості»). */
    access?: ReactNode
}

/**
 * Властивості вузла (файлу чи папки): тип, розмір або вміст папки, де
 * лежить, автор і хто змінив (з аватаром), дати, версія — і журнал вузла.
 *
 * Один компонент на два місця: бічна панель «Інформація» переглядача
 * (`DiskFileViewer`) і дровер «Властивості» екрана диска
 * (`DiskPropertiesDrawer`, той додає мініатюру й кнопки). Дані —
 * `DiskNodeInfoStore`.
 */
export const DiskNodeInfoPanel = observer(({ info, lead, actions, folderItems, access }: DiskNodeInfoPanelProps) => {
    const node = info.node

    return (
        <div data-testid="disk-info">
            {info.error !== null && <Alert type="error" showIcon title={info.error} />}
            {node === null && info.isLoading && <Skeleton active />}
            {node !== null && (
                <>
                    <div className={classNames(styles['infoHead'])}>
                        {lead ?? <DiskNodeIcon kind={node.kind} size={44} />}
                        <Title level={5} style={{ margin: 0, wordBreak: 'break-word' }} data-testid="disk-info-name">
                            {node.name}
                        </Title>
                    </div>
                    {actions && <div className={classNames(styles['infoActions'])}>{actions}</div>}
                    <Descriptions
                        column={1}
                        size="small"
                        className={classNames(styles['infoProps'])}
                        items={[
                            {
                                key: 'kind',
                                label: 'Тип',
                                children: <span title={node.mime ?? undefined}>{kindText(node)}</span>,
                            },
                            ...(node.type === 'file'
                                ? [{ key: 'size', label: 'Розмір', children: formatBytes(node.size) }]
                                : folderItems !== undefined
                                  ? [
                                        {
                                            key: 'items',
                                            label: 'Вміст',
                                            children: folderItems === null ? '…' : itemsCount(folderItems),
                                        },
                                    ]
                                  : []),
                            { key: 'path', label: 'Де лежить', children: pathOf(node) || '—' },
                            { key: 'author', label: 'Автор', children: <DiskPerson user={node.createdBy} /> },
                            { key: 'created', label: 'Створено', children: formatDateTimeUk(node.createdAt) },
                            {
                                key: 'updated',
                                label: 'Змінено',
                                children: (
                                    <span className={classNames(styles['infoUpdated'])}>
                                        <span>{formatDateTimeUk(node.updatedAt)}</span>
                                        <DiskPerson user={node.updatedBy} />
                                    </span>
                                ),
                            },
                            ...(node.currentVersion
                                ? [{ key: 'version', label: 'Версія', children: String(node.currentVersion.number) }]
                                : []),
                        ]}
                    />
                    {access && (
                        <>
                            <Title level={5} className={classNames(styles['infoSection'])}>
                                Доступ
                            </Title>
                            {access}
                        </>
                    )}
                    <Title level={5} className={classNames(styles['infoSection'])}>
                        Журнал
                    </Title>
                    <HistoryTimeline
                        entries={diskHistoryEntries(info.history)}
                        loading={info.isHistoryLoading}
                        emptyText="Подій ще немає"
                        {...(info.historyTotal > info.history.length
                            ? { note: `Показано останні ${info.history.length} з ${info.historyTotal}` }
                            : {})}
                    />
                </>
            )}
        </div>
    )
})
